<script setup lang="ts">
import { ActionButton, Card, StatusTag } from '@taler/ui'
import { onBeforeUnmount, ref } from 'vue'
import { useI18n } from 'vue-i18n'

const props = defineProps<{ code: string; canRotate: boolean; isRotating: boolean; rotateFailed: boolean }>()
const emit = defineEmits<{ rotate: [] }>()

const { t } = useI18n()
const copied = ref(false)
const confirmingRotate = ref(false)
let copiedTimer: ReturnType<typeof setTimeout> | undefined

async function copyCode(): Promise<void> {
  try {
    await navigator.clipboard.writeText(props.code)
    copied.value = true
    clearTimeout(copiedTimer)
    copiedTimer = setTimeout(() => (copied.value = false), 2000)
  } catch {
    copied.value = false
  }
}

function confirmRotate(): void {
  confirmingRotate.value = false
  emit('rotate')
}

onBeforeUnmount(() => clearTimeout(copiedTimer))
</script>

<template>
  <Card padding="block" class="flex flex-wrap items-center justify-between gap-x-6 gap-y-4">
    <div class="flex max-w-[44ch] flex-col gap-1">
      <h2 class="text-[length:var(--text-step-4)] font-semibold text-[color:var(--color-text)]">
        {{ t('relationships.ownCodeTitle') }}
      </h2>
      <p class="text-[color:var(--color-text-muted)]">{{ t('relationships.ownCodeHint') }}</p>
    </div>

    <div class="flex flex-wrap items-center gap-[var(--space-inline)]">
      <output
        class="rounded-[var(--radius-control)] border border-[color:var(--color-border-control)] bg-[color:var(--color-surface-sunken)] px-4 py-2 font-mono text-[length:var(--text-step-4)] font-semibold tracking-[0.2em] text-[color:var(--color-text)] tabular-nums"
        :aria-label="t('relationships.ownCodeValue', { code: props.code })"
      >
        {{ props.code }}
      </output>
      <ActionButton variant="secondary" @click="copyCode">
        {{ copied ? t('relationships.copied') : t('relationships.copyCode') }}
      </ActionButton>

      <template v-if="canRotate">
        <ActionButton
          v-if="!confirmingRotate"
          variant="quiet"
          :loading="isRotating"
          @click="confirmingRotate = true"
        >
          {{ t('relationships.rotateCode') }}
        </ActionButton>
        <div
          v-else
          class="flex w-full flex-col gap-2 rounded-[var(--radius-control)] bg-[color:var(--color-surface-sunken)] p-3"
        >
          <p class="text-[color:var(--color-text)]">{{ t('relationships.rotateConsequence') }}</p>
          <div class="flex flex-wrap gap-[var(--space-inline)]">
            <ActionButton variant="primary" :loading="isRotating" @click="confirmRotate">
              {{ t('relationships.rotateConfirm') }}
            </ActionButton>
            <ActionButton variant="quiet" @click="confirmingRotate = false">
              {{ t('relationships.rotateKeep') }}
            </ActionButton>
          </div>
        </div>
      </template>
    </div>

    <div v-if="rotateFailed" role="alert" class="w-full">
      <StatusTag tone="danger">{{ t('relationships.rotateFailed') }}</StatusTag>
    </div>
  </Card>
</template>
